import { readCookie } from '@zapier/cookies';

// Use globalThis to ensure the singleton persists across module boundaries
// (e.g., when code splitting creates multiple bundles)
const REFRESH_PROMISE_KEY = '__zapier_identity_refresh_promise__';

// The refresh outcome is the endpoint's HTTP status, or 0 when the request could
// not be made at all (missing CSRF token or a network error) — distinct from any
// real HTTP status so callers can treat it as transient.
export type RefreshStatus = number;

// Type-safe access to the global singleton
const getInFlightPromise = (): Promise<RefreshStatus> | null => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any)[REFRESH_PROMISE_KEY] ?? null;
};

const setInFlightPromise = (promise: Promise<RefreshStatus> | null): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any)[REFRESH_PROMISE_KEY] = promise;
};

const performRefresh = async (): Promise<RefreshStatus> => {
  try {
    const csrfToken = readCookie('csrftoken');
    if (!csrfToken) {
      console.warn('No CSRF token found for JWT refresh');
      return 0;
    }
    const refreshResponse = await fetch(`/api/v4/jwt/refresh`, {
      credentials: 'include',
      method: 'POST',
      mode: 'cors',
      headers: { 'X-CSRFToken': csrfToken },
    });
    return refreshResponse.status;
  } catch (error) {
    console.error('JWT refresh failed:', error);
    return 0;
  }
};

/**
 * De-duplicated refresh shared across all callers. This is required (not just an
 * optimization): multiple 401s can trigger simultaneous refresh calls, and all
 * callers must wait for the single real refresh to complete before retrying.
 * Returns the endpoint's HTTP status (or 0 if the request couldn't be made).
 */
export const refreshSubpathJWTStatus = async (): Promise<RefreshStatus> => {
  const existing = getInFlightPromise();
  if (existing) {
    return existing;
  }

  const promise = performRefresh().finally(() => {
    // Clear the in-flight promise once complete (success or failure)
    setInFlightPromise(null);
  });

  setInFlightPromise(promise);
  return promise;
};

export const refreshSubpathJWT = async (): Promise<boolean> => {
  // if the response is 200 the JWT was refreshed successfully
  // if the response is 429 the JWT was probably refreshed by some other tab already
  // so assuming it's refreshed and returning true.
  const status = await refreshSubpathJWTStatus();
  return status === 200 || status === 429;
};
